import { ApiClient } from './apiClient';

// ===============================
// TIPOS PARA ASIGNACIONES USUARIO-MÁQUINA
// ===============================

export interface MaquinaParaDragDrop {
  id: number;
  codigo: string;
  descripcion: string;
  tipo: string;
  marca: string;
  modelo: string;
  estado: string;
}

export interface UsuarioConMaquinas {
  usuario_uau_id: number;
  maquinas: MaquinaParaDragDrop[];
}

export interface RespuestaDatosDragDrop {
  usuarios_con_maquinas: UsuarioConMaquinas[];
  maquinas_disponibles: MaquinaParaDragDrop[];
  organizacion_id: number;
  total_usuarios: number;
  total_maquinas: number;
  maquinas_asignadas: number;
}

export interface ElementoAsignacionMaquina {
  usuario_uau_id: number;
  maquina_ids: number[];
}

export interface SolicitudAsignacionLote {
  asignaciones: ElementoAsignacionMaquina[];
  notas?: string;
}

export interface RespuestaAsignacionLote {
  exitoso: boolean;
  mensaje: string;
  estadisticas: {
    creadas: number;
    actualizadas: number;
    errores: number;
  };
  errores: string[];
}

export interface ResumenAsignaciones {
  total_maquinas: number;
  maquinas_asignadas: number;
  maquinas_disponibles: number;
  usuarios_con_asignaciones: number;
  porcentaje_asignacion: number;
}

export interface CrearAsignacionUsuarioMaquina {
  usuario_uau_id: number;
  maquina_id: number;
  notas?: string;
}

export interface VerificacionAccesoMaquina {
  usuario_uau_id: number;
  maquina_id: number;
  tiene_acceso: boolean;
  asignacion_id?: number;
  es_sin_restricciones: boolean;
}

// ===============================
// SERVICIO DE ASIGNACIONES USUARIO-MÁQUINA
// ===============================

export class AsignacionesUsuarioMaquinaService {
  private static readonly BASE_PATH = '/admin/asignaciones-usuario-maquina';

  // ===============================
  // ENDPOINTS PARA DRAG & DROP
  // ===============================

  /**
   * Obtener datos completos para la interfaz drag & drop
   * Solo accesible para superusers UAU
   */
  static async obtenerDatosDragDrop(): Promise<RespuestaDatosDragDrop> {
    return ApiClient.get<RespuestaDatosDragDrop>(`${this.BASE_PATH}/usuarios-maquinas`);
  }

  /**
   * Obtener datos para un usuario específico (máquinas disponibles filtradas)
   * Solo accesible para superusers UAU
   */
  static async obtenerDatosDragDropParaUsuario(usuarioId: number): Promise<RespuestaDatosDragDrop> {
    return ApiClient.get<RespuestaDatosDragDrop>(`${this.BASE_PATH}/usuarios-maquinas?usuario_seleccionado=${usuarioId}`);
  }

  /**
   * Guardar cambios de drag & drop mediante asignación batch
   * Solo accesible para superusers UAU
   */
  static async asignarMaquinasLote(solicitud: SolicitudAsignacionLote): Promise<RespuestaAsignacionLote> {
    return ApiClient.post<any>(`${this.BASE_PATH}/asignaciones/lote`, solicitud) as unknown as Promise<RespuestaAsignacionLote>;
  }

  /**
   * Obtener máquinas disponibles para asignación
   * Solo accesible para superusers UAU
   */
  static async obtenerMaquinasDisponibles(excluirUsuarioId?: number): Promise<MaquinaParaDragDrop[]> {
    const params = excluirUsuarioId ? `?excluir_usuario_id=${excluirUsuarioId}` : '';
    return ApiClient.get<MaquinaParaDragDrop[]>(`${this.BASE_PATH}/maquinas-disponibles${params}`);
  }

  // ===============================
  // ENDPOINTS INDIVIDUALES
  // ===============================

  /**
   * Crear asignación individual usuario-máquina
   * Solo accesible para superusers UAU
   */
  static async crearAsignacion(datos: CrearAsignacionUsuarioMaquina): Promise<any> {
    const response = await ApiClient.post(`${this.BASE_PATH}/asignaciones`, datos);
    return response.data;
  }

  /**
   * Eliminar asignación individual usuario-máquina
   * Solo accesible para superusers UAU
   */
  static async eliminarAsignacion(usuarioUauId: number, maquinaId: number): Promise<void> {
    await ApiClient.delete(`${this.BASE_PATH}/asignaciones/${usuarioUauId}/maquinas/${maquinaId}`);
  }

  // ===============================
  // ENDPOINTS DE ESTADÍSTICAS
  // ===============================

  /**
   * Obtener resumen de asignaciones para dashboard administrativo
   * Solo accesible para superusers UAU
   */
  static async obtenerResumenAsignaciones(): Promise<ResumenAsignaciones> {
    return ApiClient.get<ResumenAsignaciones>(`${this.BASE_PATH}/asignaciones/resumen`);
  }

  // ===============================
  // ENDPOINTS PARA USUARIO ACTUAL
  // ===============================

  /**
   * Obtener máquinas asignadas al usuario actual para filtros de reportes
   * Accesible para cualquier usuario autenticado
   */
  static async obtenerMisMaquinasAsignadas(): Promise<MaquinaParaDragDrop[]> {
    return ApiClient.get<MaquinaParaDragDrop[]>('/reportes/mis-maquinas');  // ✅ ENDPOINT CORRECTO
  }

  /**
   * Verificar si el usuario actual tiene acceso a una máquina específica
   * Usado para filtrado automático en otros módulos
   */
  static async verificarAccesoMaquina(maquinaId: number): Promise<VerificacionAccesoMaquina> {
    return ApiClient.get<VerificacionAccesoMaquina>(`${this.BASE_PATH}/verificar-acceso/${maquinaId}`);
  }

  // ===============================
  // UTILIDADES Y HELPERS
  // ===============================

  /**
   * Convertir datos del drag & drop al formato de solicitud batch
   */
  static convertirDragDropALote(usuariosConMaquinas: UsuarioConMaquinas[], notas?: string): SolicitudAsignacionLote {
    const asignaciones: ElementoAsignacionMaquina[] = usuariosConMaquinas.map(usuario => ({
      usuario_uau_id: usuario.usuario_uau_id,
      maquina_ids: usuario.maquinas.map(m => m.id)
    }));

    return {
      asignaciones,
      notas
    };
  }

  /**
   * Obtener estadísticas básicas de las asignaciones
   */
  static calcularEstadisticas(datos: RespuestaDatosDragDrop): {
    usuariosConMaquinas: number;
    maquinasSinAsignar: number;
    totalAsignaciones: number;
    porcentajeCobertura: number;
  } {
    const usuariosConMaquinas = datos.usuarios_con_maquinas.length;
    const maquinasSinAsignar = datos.maquinas_disponibles.length;
    const totalAsignaciones = datos.usuarios_con_maquinas.reduce(
      (total, usuario) => total + usuario.maquinas.length, 
      0
    );
    const porcentajeCobertura = datos.total_maquinas > 0 
      ? Math.round((datos.maquinas_asignadas / datos.total_maquinas) * 100)
      : 0;

    return {
      usuariosConMaquinas,
      maquinasSinAsignar,
      totalAsignaciones,
      porcentajeCobertura
    };
  }

  /**
   * Validar que las asignaciones son correctas antes del envío
   */
  static validarAsignaciones(solicitud: SolicitudAsignacionLote): {
    esValida: boolean;
    errores: string[];
    advertencias: string[];
  } {
    const errores: string[] = [];
    const advertencias: string[] = [];

    // Verificar que hay asignaciones
    if (!solicitud.asignaciones || solicitud.asignaciones.length === 0) {
      advertencias.push('No hay asignaciones para procesar');
    }

    // Verificar duplicados de máquinas
    const maquinasAsignadas = new Set<number>();
    const maquinasDuplicadas = new Set<number>();

    for (const asignacion of solicitud.asignaciones) {
      if (!asignacion.usuario_uau_id) {
        errores.push('Todas las asignaciones deben tener un usuario válido');
        continue;
      }

      for (const maquinaId of asignacion.maquina_ids) {
        if (maquinasAsignadas.has(maquinaId)) {
          maquinasDuplicadas.add(maquinaId);
        } else {
          maquinasAsignadas.add(maquinaId);
        }
      }
    }

    if (maquinasDuplicadas.size > 0) {
      errores.push(`Máquinas asignadas a múltiples usuarios: ${Array.from(maquinasDuplicadas).join(', ')}`);
    }

    return {
      esValida: errores.length === 0,
      errores,
      advertencias
    };
  }

  // ===============================
  // HOOKS PARA REACT QUERY (OPCIONALES)
  // ===============================

  /**
   * Query keys para React Query
   */
  static readonly queryKeys = {
    all: ['asignaciones-usuario-maquina'] as const,
    dragDrop: () => [...this.queryKeys.all, 'drag-drop'] as const,
    misMaquinas: () => [...this.queryKeys.all, 'mis-maquinas'] as const,
    resumen: () => [...this.queryKeys.all, 'resumen'] as const,
    maquinasDisponibles: () => [...this.queryKeys.all, 'disponibles'] as const,
    accesoMaquina: (maquinaId: number) => [...this.queryKeys.all, 'acceso', maquinaId] as const,
  };

  /**
   * Mutación keys para React Query
   */
  static readonly mutationKeys = {
    asignarLote: 'asignar-lote' as const,
    crearAsignacion: 'crear-asignacion' as const,
    eliminarAsignacion: 'eliminar-asignacion' as const,
  };
}
